/**
 * Generates a task path for a given pull request global ID.
 * @param globalId - The global ID of the pull request.
 * @param sessionId - The ID of the session to be focused on page load.
 * @returns The task path URL, or an empty string if globalId is empty.
 */
export const generateTaskPath = (globalId: string, sessionId?: string) => {
  if (!globalId) {
    return ''
  }

  let taskPath = `/copilot/tasks/pull/${encodeURIComponent(globalId)}`
  if (sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}

export interface GenerateTaskPathForRepoOptions {
  /** The repository's name with owner (e.g., "owner/repo"). */
  repositoryNwo?: string
  /** The task ID for direct task linking. */
  taskId?: string
  /** The pull request number. */
  pullRequestNumber?: number
  /** The ID of the session to be focused on page load. */
  sessionId?: string
  /** When true, generates /copilot/t/{nwo}/{taskId} URLs. Takes precedence over other URL types. */
  isUsingIntentDetection?: boolean
}

/**
 * Generates a task path within a specific repository context.
 * @param options - The options for generating the task path.
 * @returns The task path URL, or an empty string if repository data is unavailable.
 */
export const generateTaskPathForRepo = ({
  repositoryNwo,
  pullRequestNumber,
  sessionId,
  taskId,
  isUsingIntentDetection = false,
}: GenerateTaskPathForRepoOptions) => {
  // Intent detection URLs take precedence over all other URL types
  if (isUsingIntentDetection && repositoryNwo && taskId) {
    return `/copilot/t/${repositoryNwo}/${encodeURIComponent(taskId)}`
  }

  let taskPath = ''
  if (repositoryNwo) {
    if (taskId) {
      taskPath = `/${repositoryNwo}/tasks/${encodeURIComponent(taskId)}`
    } else if (pullRequestNumber) {
      taskPath = `/${repositoryNwo}/agents/pull/${encodeURIComponent(pullRequestNumber)}`
    }
  }

  if (taskPath && sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}
